
import React, { useState, useEffect } from "react";
import { 
  Table, TableHeader, TableBody, TableRow, 
  TableHead, TableCell, TableCaption,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { LayoutLoader } from "@/components/ui/layout-loader";
import { FileText, Search, DollarSign, Calendar, Filter } from "lucide-react";
import { 
  AngkasaMember,
  AngkasaPurchase,
  getMember, 
  getMemberPurchases,
  getAllMembers 
} from "@/lib/db/angkasaMemberDb";

const MemberPurchaseHistoryTab = () => {
  const [members, setMembers] = useState<AngkasaMember[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>("");
  const [selectedMember, setSelectedMember] = useState<AngkasaMember | null>(null);
  const [purchases, setPurchases] = useState<AngkasaPurchase[]>([]);
  const [filteredPurchases, setFilteredPurchases] = useState<AngkasaPurchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchasesLoading, setIsPurchasesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [amountFilter, setAmountFilter] = useState("all");
  const [selectedPurchase, setSelectedPurchase] = useState<AngkasaPurchase | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const data = await getAllMembers();
        setMembers(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to fetch members", error);
        toast({
          variant: "destructive",
          title: "Fetch Error",
          description: "Failed to fetch member data.",
        });
        setIsLoading(false);
      }
    };

    fetchMembers();
  }, [toast]);

  const fetchMemberPurchases = async (memberId: string) => {
    if (!memberId) return;
    
    setIsPurchasesLoading(true);
    try {
      const member = await getMember(memberId);
      setSelectedMember(member);
      
      const purchaseData = await getMemberPurchases(memberId);
      setPurchases(purchaseData);
      setFilteredPurchases(purchaseData);
      setIsPurchasesLoading(false);
    } catch (error) {
      console.error("Failed to fetch member purchases", error);
      toast({
        variant: "destructive",
        title: "Fetch Error",
        description: "Failed to fetch purchase history.",
      });
      setIsPurchasesLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMemberId) {
      fetchMemberPurchases(selectedMemberId);
    }
  }, [selectedMemberId]);

  useEffect(() => {
    filterPurchases();
  }, [purchases, searchTerm, dateFilter, amountFilter]);

  const filterPurchases = () => {
    let filtered = [...purchases];

    // Apply search term filter (could search by product name in the items array)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(purchase => 
        purchase.items.some(item => 
          item.productName.toLowerCase().includes(searchLower)
        ) ||
        purchase.id.toLowerCase().includes(searchLower)
      );
    }

    // Apply date filter
    if (dateFilter !== "all") {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(today.getDate() - 30);
      const thirtyDaysAgoStr = thirtyDaysAgo.toISOString().split('T')[0];
      const ninetyDaysAgo = new Date(today);
      ninetyDaysAgo.setDate(today.getDate() - 90);
      const ninetyDaysAgoStr = ninetyDaysAgo.toISOString().split('T')[0];
      
      if (dateFilter === "last30") {
        filtered = filtered.filter(purchase => purchase.date >= thirtyDaysAgoStr);
      } else if (dateFilter === "last90") {
        filtered = filtered.filter(purchase => purchase.date >= ninetyDaysAgoStr);
      }
    }

    // Apply amount filter
    if (amountFilter !== "all") {
      if (amountFilter === "under50") {
        filtered = filtered.filter(purchase => purchase.amount < 50);
      } else if (amountFilter === "50to100") {
        filtered = filtered.filter(purchase => purchase.amount >= 50 && purchase.amount <= 100);
      } else if (amountFilter === "over100") {
        filtered = filtered.filter(purchase => purchase.amount > 100);
      }
    }

    setFilteredPurchases(filtered);
  };

  if (isLoading) {
    return <LayoutLoader message="Loading member data..." />;
  }

  return (
    <div className="space-y-4">
      {!selectedMemberId ? (
        <div className="flex flex-col items-center p-8 bg-background border rounded-md">
          <FileText className="h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-xl font-medium">Select a Member to View Purchase History</h3>
          <p className="text-muted-foreground mb-6">Choose a member from the dropdown below to view their purchase history.</p>
          
          <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
            <SelectTrigger className="w-[350px]">
              <SelectValue placeholder="Select a member" />
            </SelectTrigger>
            <SelectContent>
              {members.map(member => (
                <SelectItem key={member.memberId} value={member.memberId}>
                  {member.memberId} - {member.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <>
          {isPurchasesLoading ? (
            <LayoutLoader message="Loading purchase history..." />
          ) : (
            <>
              <div className="flex flex-col md:flex-row gap-4 items-start">
                <div className="bg-background border rounded-md p-4 md:w-1/3">
                  <h3 className="font-medium text-lg mb-4">Member Information</h3>
                  
                  {selectedMember && (
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Member ID:</span>
                        <span className="font-medium">{selectedMember.memberId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Name:</span>
                        <span className="font-medium">{selectedMember.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Cooperative:</span>
                        <span className="font-medium">{selectedMember.cooperativeName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Member Type:</span>
                        <span className="font-medium">{selectedMember.memberType}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Promotion Group:</span>
                        <Badge variant={
                          selectedMember.promotionGroup === "Gold" ? "default" : 
                          selectedMember.promotionGroup === "Silver" ? "secondary" : "outline"
                        }>
                          {selectedMember.promotionGroup}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total Spent:</span>
                        <span className="font-medium text-green-600">RM {selectedMember.totalSpent.toFixed(2)}</span>
                      </div>
                      
                      <div className="pt-2 flex justify-between">
                        <Button variant="outline" onClick={() => setSelectedMemberId("")}>
                          Change Member
                        </Button>
                        <Button variant="outline">Print History</Button>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="grid gap-4 md:grid-cols-3 mb-4">
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Transactions</CardTitle>
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{purchases.length}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Average Value</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          RM {purchases.length > 0 
                            ? (purchases.reduce((acc, curr) => acc + curr.amount, 0) / purchases.length).toFixed(2) 
                            : "0.00"}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Last Purchase</CardTitle>
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {purchases.length > 0 
                            ? new Date(Math.max(...purchases.map(p => new Date(p.date).getTime()))).toLocaleDateString() 
                            : "N/A"}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
                    <div className="flex flex-1 items-center gap-2">
                      <Search className="h-5 w-5 text-muted-foreground" />
                      <Input
                        placeholder="Search by product or receipt ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                      <Filter className="h-4 w-4 text-muted-foreground" />
                      <Select value={dateFilter} onValueChange={setDateFilter}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Date Range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Time</SelectItem>
                          <SelectItem value="last30">Last 30 Days</SelectItem>
                          <SelectItem value="last90">Last 90 Days</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Select value={amountFilter} onValueChange={setAmountFilter}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Amount" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Amounts</SelectItem>
                          <SelectItem value="under50">Under RM50</SelectItem>
                          <SelectItem value="50to100">RM50 - RM100</SelectItem>
                          <SelectItem value="over100">Over RM100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="rounded-md border">
                    <Table>
                      <TableCaption>Purchase history for {selectedMember?.name}</TableCaption>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Receipt ID</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Amount (RM)</TableHead>
                          <TableHead>Items</TableHead>
                          <TableHead>Promo Applied</TableHead>
                          <TableHead>Savings (RM)</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPurchases.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-4">No purchase records found</TableCell>
                          </TableRow>
                        ) : (
                          filteredPurchases.map((purchase) => (
                            <TableRow key={purchase.id}>
                              <TableCell className="font-medium">{purchase.id}</TableCell>
                              <TableCell>{purchase.date}</TableCell>
                              <TableCell>{purchase.amount.toFixed(2)}</TableCell>
                              <TableCell>{purchase.items.length} items</TableCell>
                              <TableCell>
                                {purchase.promoApplied ? (
                                  <Badge variant="outline">{purchase.promoApplied}</Badge>
                                ) : (
                                  <span className="text-muted-foreground">None</span>
                                )}
                              </TableCell>
                              <TableCell>{purchase.promoSavings.toFixed(2)}</TableCell>
                              <TableCell>
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => setSelectedPurchase(purchase)}
                                    >
                                      Details
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="sm:max-w-[600px]">
                                    <DialogHeader>
                                      <DialogTitle>Purchase Details</DialogTitle>
                                    </DialogHeader>
                                    {selectedPurchase && (
                                      <div className="space-y-4">
                                        <div className="flex justify-between">
                                          <span className="font-medium">Receipt ID:</span>
                                          <span>{selectedPurchase.id}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="font-medium">Date:</span>
                                          <span>{selectedPurchase.date}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="font-medium">Store ID:</span>
                                          <span>{selectedPurchase.storeId}</span>
                                        </div>
                                        <div className="border-t pt-2">
                                          <h4 className="font-medium mb-2">Items Purchased:</h4>
                                          <Table>
                                            <TableHeader>
                                              <TableRow>
                                                <TableHead>Product</TableHead>
                                                <TableHead>Qty</TableHead>
                                                <TableHead>Unit Price</TableHead>
                                                <TableHead>Total</TableHead>
                                              </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                              {selectedPurchase.items.map((item, index) => (
                                                <TableRow key={index}>
                                                  <TableCell>{item.productName}</TableCell>
                                                  <TableCell>{item.quantity}</TableCell>
                                                  <TableCell>RM {item.unitPrice.toFixed(2)}</TableCell>
                                                  <TableCell>RM {item.total.toFixed(2)}</TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </div>
                                        <div className="border-t pt-2">
                                          <div className="flex justify-between">
                                            <span className="font-medium">Subtotal:</span>
                                            <span>RM {selectedPurchase.amount.toFixed(2)}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="font-medium">Promotion Applied:</span>
                                            <span>{selectedPurchase.promoApplied || "None"}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="font-medium">Savings:</span>
                                            <span className="text-green-600">RM {selectedPurchase.promoSavings.toFixed(2)}</span>
                                          </div>
                                          <div className="flex justify-between text-lg font-bold mt-2">
                                            <span>Total:</span>
                                            <span>RM {(selectedPurchase.amount - selectedPurchase.promoSavings).toFixed(2)}</span>
                                          </div>
                                        </div>
                                        <div className="flex justify-end mt-2">
                                          <Button variant="outline">Print Receipt</Button>
                                        </div>
                                      </div>
                                    )}
                                  </DialogContent>
                                </Dialog>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MemberPurchaseHistoryTab;
